import {
    Bundle,
    BundleStamp,
    BundleStampFlavor,
    BundleStub,
    ManifestType,
    PackingMode,
    useFullWastesForBundle,
    Waste,
    watchBundleByIdTqo,
    watchJobByIdTqo,
    watchManifestTypesTqo,
    watchPackingModesTqo,
    watchStampsForBundleTqo
} from "../rpc/webrpc-client.gen.ts";
import {isTstzFinite} from "../tstzspan.ts";
import {useTranslation} from "react-i18next";
import {useCallback, useContext, useMemo} from "react";
import {useQueryClient} from "@tanstack/react-query";
import {ActionButtonProps} from "../components/ActionButton.tsx";
import {useNavigate} from "@tanstack/react-router";
import {useMountedTruck} from "../driver/mountedTruck.ts";
import {useTruckStatus} from "../truck/truck.tsx";
import {
    useAttachPhotoToStampMutation,
    useCompleteBundleOffloadingMutation,
    useCompleteBundleOnloadingMutation,
    useMothership,
    useSchedulePickupMutation,
    useSpawnGroundedBundleMutation,
    useStampBundleMutation,
    useStartBundleOnloadingMutation,
    useStartTriplegMutation
} from "../tq/tqx.tsx";
import {useSelf} from "../agent/useSelf.ts";
import {DEFAULT_PACKING_MODE_CODE} from "../waste/waste.tsx";
import {LocalPhoto, uploadPhoto} from "../photo.tsx";
import {uuidv7} from "uuidv7";
import {useAuthenticator} from "../auth/authenticator.tsx";
import {z} from "zod/v4";
import {BundleStatusCode, useBundleStatus} from "./status.tsx";
import {ConfirmModalContext} from "../confirmModalProps.tsx";
import {useRefreshableInfallibleQuery} from "../util/tqrefresh.tsx";
import {DEADBEEF, InfallibleUseResult, pendingUseResult, successUseResult} from "../util/hookutil.tsx";
import {Keyed, keyify} from "../util/arutil.tsx";
import {useBomb} from "../bomb/bomb.tsx";
import {useInfallibleQuery} from "../util/useInfallibleQuery.tsx";

export interface UseFlavoredStampForBundleArgs {
    bundleId: string;
    flavor: BundleStampFlavor;
}

export function useFlavoredBundleStamp(args?: UseFlavoredStampForBundleArgs): InfallibleUseResult<BundleStamp | null> {
    const mama = useMothership();
    return useRefreshableInfallibleQuery({
        ...watchStampsForBundleTqo(mama, args),
        select: (rpc) =>
            rpc.stamps.find(maybe => maybe.flavor === args?.flavor) ?? null,
    });
}

// export function useIsFieldProposed({bundleId, ...rest}: LimitedUseQueryOptions<{ bundleId: string }>) {
//     const {data, isSuccess}
//         = useRefreshableQuery({...activeStampsForBundleTqo({bundleId}), ...rest});
//     if (!isSuccess) {
//         return {isSuccess};
//     }
//     const stamps = data.stamps ?? NADA;
//     return {
//         data: !!stamps.find(maybe => maybe.flavor === BundleStampFlavor.FIELD_COMPLETE_PROPOSE)
//     }
//
//     export function useIsFieldApproved({bundleId, enabled, ...rest}: LimitedUseQueryOptions<{ bundleId: string }>) {
//         const {data, isSuccess}
//             = useRefreshableQuery({...activeStampsForBundleTqo({bundleId}), enabled, ...rest});
//         if (!isSuccess) {
//             return {isSuccess};
//         }
//         const stamps = data.stamps;
//         return {
//             isSuccess: true,
//             data: !!stamps.find(maybe => maybe.flavor === BundleStampFlavor.FIELD_COMPLETE_ACCEPT),
//         };
//     }
//
//     export function useIsDelivered({bundleId, enabled, ...rest}: LimitedUseQueryOptions<{ bundleId: string }>) {
//         const {data, isSuccess}
//             = useRefreshableQuery({...activeStampsForBundleTqo({bundleId}), enabled, ...rest});
//         if (!isSuccess) {
//             return {isSuccess};
//         }
//         const stamps = data.stamps;
//         return {
//             isSuccess: true,
//             data: !!stamps.find(maybe => maybe.flavor === BundleStampFlavor.DELIVERED),
//         };
//     }

export function useBundleBagTotal(args?: { bundleId: string }): InfallibleUseResult<number> {
    const wastesTqrv = useFullWastesForBundle(args);
    const rv = useMemo(() => {
        if (wastesTqrv.isPending) {
            return pendingUseResult;
        }
        return successUseResult(wastesTqrv.data.reduce((acc, next) => {
            if (next?.bags) {
                acc += next.bags;
            }
            return acc;
        }, 0));
    }, [wastesTqrv.data, wastesTqrv.isPending]);
    return rv;
}

// function useBundleHistoryDownloadFilename(args?: { bundleId: string }) {
//     const mama = useMothership();
//
//     const select = useCallback((bundle: Bundle) => {
//         if (!args) {
//             return pendingResult;
//         }
//         const parts = [
//             bundle.displayName,
//             args.bundleId,
//             format(new Date(), "yyyy.MM.dd.HH.mm.ss"),
//         ];
//         const rv = join(parts, "-") + ".json";
//         return successResult(rv);
//     }, [args]);
//
//     return useQuery({...lastValidBundleByIdTqo(mama, args), select});
// }

// export function useBundleHistoryDownloadProps({bundleId}: { bundleId: string }) {
//     const {t} = useTranslation();
//     const history = useCompleteBundleHistoryById({bundleId});
//     const filename = useBundleHistoryDownloadFilename({bundleId});
//
//     const perform = useCallback(() =>
//             Promise.resolve({data: history.data, filename: filename.data}),
//         [history, filename]);
//
//     return {
//         color: "primary", variant: "solid",
//         disabled: !history,
//         perform,
//         children: t("downloadCompleteBundleHistory")
//     } as const;
// }

export function useBundleHistoryViewProps({bundleId}: { bundleId: string }): ActionButtonProps {
    const {t} = useTranslation();
    const navigate = useNavigate();

    return {
        color: "primary", variant: "solid",
        disabled: !history,
        perform: () => navigate({
            to: "/history/bundle/$id",
            params: {id: bundleId},
        }),
        children: t("downloadCompleteBundleHistory"),
    };
}

const commonActionProps = {color: "success", variant: "solid"} as const;

function useSuccessorBundleSpawner(args?: { bundle: Bundle }): InfallibleUseResult<() => Promise<Bundle>> {
    const bundle = args?.bundle;
    const selfTqrv = useSelf();
    const self = selfTqrv.data;
    const mountedTruckTqrv = useMountedTruck();
    const mountedTruck = mountedTruckTqrv.data;
    const {mutateAsync: spawnBundle} = useSpawnGroundedBundleMutation();
    const performSpawn = useCallback(() => {
        if (bundle) {
            return spawnBundle({
                jobId: bundle.job.id,
                originVenueId: bundle.origin.venue!.id,
                destinationId: bundle.destination!.id,
                wastes: [],
            });
        } else {
            throw new Error("not ready yet");
        }
    }, [bundle, spawnBundle]);
    const {mutateAsync: schedulePickup} = useSchedulePickupMutation();
    const performSchedulePickup = useCallback(({newBundleId}: { newBundleId: string }) => {
        return schedulePickup({
            food: {
                bundleId: newBundleId,
                truckId: mountedTruck!.id,
                driverId: self!.id,
            }
        })
    }, [mountedTruck, schedulePickup, self]);
    const rv = useCallback(() =>
            performSpawn()
                .then(({bundle: newBundle}) => {
                    return performSchedulePickup({newBundleId: newBundle.id})
                        .then(() => {
                            return newBundle;
                        });
                }),
        [performSchedulePickup, performSpawn]);
    if (selfTqrv.isPending || mountedTruckTqrv.isPending) {
        return pendingUseResult;
    } else {
        return successUseResult(rv);
    }
}

export function useBundleActions(args: { bundleId: string }): InfallibleUseResult<Keyed<ActionButtonProps>[] | null> {
    const bundleId = args.bundleId;
    const authenticator = useAuthenticator();
    const queryClient = useQueryClient();
    const mama = useMothership();
    const bundleTqrv = useRefreshableInfallibleQuery(watchBundleByIdTqo(mama, {bundleId}));
    const bundle = bundleTqrv.data;
    const bundleStatusTqrv = useBundleStatus({bundleId});
    const bundleStatus = bundleStatusTqrv.data;

    const selfTqrv = useSelf();
    const self = selfTqrv.data;
    const mountedTruckTqrv = useMountedTruck();
    const mountedTruck = mountedTruckTqrv.data;
    const truckStatusArgs = mountedTruck ? {truckId: mountedTruck.id} : undefined;
    const truckStatusTqrv = useTruckStatus(truckStatusArgs);
    const mountedTruckStatus = truckStatusTqrv.data;
    const navigate = useNavigate();

    const confirmModalContext = useContext(ConfirmModalContext);

    const {mutateAsync: startOnloading} = useStartBundleOnloadingMutation();
    const performStartOnloading = useCallback(() => {
        if (!mountedTruck) {
            throw new Error("ghost truck onloading");
        }
        return startOnloading({
            bundleId, truckId: mountedTruck.id,
        }).then(() => navigate({
            to: "/bundle/$id/cargo",
            params: {id: bundleId},
        }))
    }, [bundleId, mountedTruck, navigate, startOnloading]);

    const {mutateAsync: sendStampBundle} = useStampBundleMutation();
    const sendFlavoredStamp = useCallback((flavor: BundleStampFlavor) => {
        return sendStampBundle({bundleId, flavor});
    }, [bundleId, sendStampBundle]);
    const {mutateAsync: sendCompleteOnloading} = useCompleteBundleOnloadingMutation();

    const performSeekFieldApproval = useCallback(() => navigate({
        to: "/bundle/$id/cargo",
        params: {id: bundleId},
    }), [bundleId, navigate]);
    const performCompleteOnloading = useCallback(() =>
            sendCompleteOnloading({bundleId})
                .then(() => sendFlavoredStamp("FIELD_COMPLETE_PROPOSE"))
                .then(performSeekFieldApproval),
        [bundleId, performSeekFieldApproval, sendCompleteOnloading, sendFlavoredStamp]);

    const {mutateAsync: startTripleg} = useStartTriplegMutation();
    const performDepartForDelivery = useCallback(() => {
        if (!bundle?.destination?.id) {
            throw new Error("deadbeef trying to get loose");
        }
        if (!mountedTruck) {
            throw new Error("ghost truck trying to depart");
        }
        return startTripleg({destinationId: bundle.destination.id})
            .then(() => navigate({
                to: "/truck/$id/drive",
                params: {id: mountedTruck.id},
            }))
    }, [bundle?.destination?.id, mountedTruck, navigate, startTripleg]);

//    const offloadVenueId = lastTransfer?.offloadVenue?.id ?? bundle?.destination?.id ?? TQDEADBEEF;
    const {mutateAsync: sendCompleteOffloading} = useCompleteBundleOffloadingMutation();
    const completeOffloading = useCallback(() => sendCompleteOffloading({bundleId}),
        [bundleId, sendCompleteOffloading]);
    const stampDelivered = useCallback(() =>
            sendFlavoredStamp("DELIVERED"),
        [sendFlavoredStamp]);
    const {mutateAsync: performAttachPhotoToStamp} = useAttachPhotoToStampMutation();
    const performCompleteOffloading = useCallback((sigBlob$: Promise<Blob | null>) =>
            completeOffloading()
                .then(() => Promise.all([
                    stampDelivered(),
                    sigBlob$
                ]))
                .then(([stampDeliverTqrv, sigBlob]) => {
                    if (sigBlob) {
                        const lp: LocalPhoto = {id: uuidv7(), file: sigBlob};
                        return uploadPhoto({authenticator, queryClient, photo: lp})
                            .then((uploadResult) => performAttachPhotoToStamp({
                                subjectClass: "Bundle",
                                subjectId: bundleId,
                                stampId: stampDeliverTqrv.stamp.id,
                                photoSlug: uploadResult.photoSlug
                            }));
                    }
                })
                .then(() => navigate({to: "/"})),
        [authenticator, bundleId, completeOffloading, navigate, performAttachPhotoToStamp, queryClient, stampDelivered]);

    const {data: spawnSuccessor}
        = useSuccessorBundleSpawner({bundle: bundle!});
    const performPostponeFinal = useCallback(() =>
        spawnSuccessor!()
            .then(() => sendFlavoredStamp("FIELD_COMPLETE_ACCEPT"))
            .then(() => navigate({to: "/driver/next", params: {}, replace: true})
            ), [navigate, sendFlavoredStamp, spawnSuccessor]);

    const {t} = useTranslation();
    const bomb = useBomb();

    const actions = useMemo<Keyed<ActionButtonProps>[] | null>(() => {
            switch (bundleStatus?.code) {
                case undefined:
                case BundleStatusCode.enum.UNKNOWN:
                case BundleStatusCode.enum.DELIVERED:
                case BundleStatusCode.enum.INACTIVE:
                    return null;

                case BundleStatusCode.enum.INSPECTED:
                    // TODO manual export and hold
                    return null;

                case BundleStatusCode.enum.AWAITING_PICKUP:
                    return keyify([{
                        ...commonActionProps,
                        perform: performStartOnloading,
                        children: t("driver.startOnloading")
                    }]);

                case BundleStatusCode.enum.ONLOADING:
                    return keyify([{
                        ...commonActionProps,
                        perform: () => {
                            confirmModalContext.open({
                                question: t("driver.reallyCompleteOnloadPrompt"),
                                yesAnswer: t("driver.yesReallyCompleteOnload"),
                                onYes: () => {
                                    void performCompleteOnloading();
                                },
                            });
                            return Promise.resolve();
                        },
                        children: t("driver.completeOnload"),
                    }]);

                case BundleStatusCode.enum.OFFLOADING:
                    return keyify([{
                        ...commonActionProps,
                        perform: () => {
                            if (mountedTruck) {
                                // this is a bit fragile, but i can't think of a more
                                // reliable way to know whether we should ask for a signature
                                // here. what we really want to know is whether we admitted
                                // ourselves or not
                                if (mountedTruckStatus?.lastTripleg?.lastModifier.id === self?.id) {
                                    confirmModalContext.open({
                                        question: t("driver.reallyCompleteOffloadPrompt"),
                                        yesAnswer: t("driver.yesReallyCompleteOffload"),
                                        signatureWanted: true,
                                        onYes: (sigBlob$) => {
                                            performCompleteOffloading(sigBlob$)
                                                .catch((xc: unknown) => {
                                                    const error = xc instanceof Error ? xc :
                                                        new Error("photo upload failed", {cause: xc});
                                                    bomb(error);
                                                });
                                        },
                                    });
                                    return Promise.resolve();
                                } else {
                                    return performCompleteOffloading(Promise.resolve(null))
                                        .catch((xc: unknown) => {
                                            const error = xc instanceof Error ? xc :
                                                new Error("photo upload failed", {cause: xc});
                                            bomb(error);
                                        });
                                }
                            } else {
                                confirmModalContext.open({
                                    question: t("driver.reallyCompleteOffloadPrompt"),
                                    yesAnswer: t("driver.yesReallyCompleteOffload"),
                                    onYes: () => {
                                        performCompleteOffloading(Promise.resolve(null))
                                            .catch((xc: unknown) => {
                                                const error = xc instanceof Error ? xc :
                                                    new Error("photo upload failed", {cause: xc});
                                                bomb(error);
                                            });
                                    },
                                });
                                return Promise.resolve();
                            }
                        },
                        children: mountedTruck ? t("driver.offload") : t("driver.completeOffload"),
                    }]);

                case "ONLOADED":
                    return keyify([{
                        ...commonActionProps,
                        perform: performPostponeFinal,
                        children: t("driver.performPostponeFinal")
                    }]);

                case BundleStatusCode.enum.FIELD_APPROVED: {
                    if (!bundle?.destination?.id) {
                        return null;
                    }
                    return keyify([{
                        ...commonActionProps,
                        ...(mountedTruckStatus?.currentDestination
                            ? {
                                disabled: true,
                                children: t("driver.inTransit"),
                            }
                            : (mountedTruckStatus?.currentPlace?.id === bundle.destination.id
                                ? // this is the 持ち込み case
                                {
                                    disabled: !isTstzFinite(mountedTruckStatus.lastTripleg?.admitted),
                                    perform: () => performCompleteOffloading(Promise.resolve(null)),
                                    children: t("driver.offload")
                                }
                                : (mountedTruckStatus?.canDepart
                                    ? {
                                        disabled: !bundle.destination.id,
                                        perform: performDepartForDelivery,
                                        children: t("driver.depart")
                                    } : null))),
                    }]);
                }
            }
        }, [bomb, bundle?.destination?.id, bundleStatus?.code, confirmModalContext,
            mountedTruck, mountedTruckStatus?.canDepart, mountedTruckStatus?.currentDestination,
            mountedTruckStatus?.currentPlace?.id, mountedTruckStatus?.lastTripleg?.admitted,
            mountedTruckStatus?.lastTripleg?.lastModifier.id,
            performCompleteOffloading, performCompleteOnloading,
            performDepartForDelivery, performPostponeFinal, performStartOnloading, self?.id, t]
    );

    // don't insist on mounted truck or successor spawn here, this can be used by intakers

    if (!bundleStatusTqrv.isSuccess || !bundleTqrv.isSuccess) {
        return pendingUseResult;
    }

    return successUseResult(actions);
}

export const WasteDefaults = z.object({
    packingMode: z.nullable(PackingMode),
    manifestType: z.nullable(ManifestType),
});
export type WasteDefaults = z.infer<typeof WasteDefaults>;

export function useBundleWasteDefaults(args?: { bundleId: string }): InfallibleUseResult<WasteDefaults> {
    const mama = useMothership();
    const bundleTqrv = useInfallibleQuery(watchBundleByIdTqo(mama, args));
    const jobTqrv = useInfallibleQuery(watchJobByIdTqo(mama, (
        bundleTqrv.data?.job.id
            ? {jobId: bundleTqrv.data.job.id}
            : undefined)));
    const job = jobTqrv.data;

    const allManifestTypesTqrv = useInfallibleQuery(watchManifestTypesTqo(mama));
    const allManifestTypes = allManifestTypesTqrv.data;

    let jobManifestType: ManifestType | null = null;
    const jobExtras = job?.extras;
    const jobManifestTypeCode = jobExtras?.shinwa?.manifestTypeCode ?? jobExtras?.sanyu?.manifestTypeCode;
    if (!!jobManifestTypeCode && !!allManifestTypes) {
        const wantedCode = jobManifestTypeCode;
        for (const mt of allManifestTypes) {
            if (mt.code === wantedCode) {
                jobManifestType = mt;
                break;
            }
        }
    }
    const packingModesTqrv = useInfallibleQuery(watchPackingModesTqo(mama));
    const allPackingModes = packingModesTqrv.data;
    let defaultPackingMode: PackingMode | null = null;
    if (allPackingModes) {
        const wantedCode = DEFAULT_PACKING_MODE_CODE;
        for (const mode of allPackingModes) {
            if (mode.code === wantedCode) {
                defaultPackingMode = mode;
                break;
            }
        }
    }

    const rv = useMemo(() => {
        if (allManifestTypesTqrv.isPending || packingModesTqrv.isPending) {
            return pendingUseResult;
        }
        return successUseResult({
            manifestType: jobManifestType,
            packingMode: defaultPackingMode,
        });
    }, [allManifestTypesTqrv.isPending, defaultPackingMode,
        jobManifestType, packingModesTqrv.isPending]);

    return rv;
}

export function useBundleContinuingWastes(args?: { bundleId: string }): InfallibleUseResult<Waste[]> {
    const mama = useMothership();
    const bundleDestinationTqrv = useInfallibleQuery({
        ...watchBundleByIdTqo(mama, args),
        select: (rpc) => rpc.bundle?.destination?.id,
    });
    const bundleDestinationId = bundleDestinationTqrv.data;
    const wastesTqrv = useFullWastesForBundle(args);

    if (bundleDestinationTqrv.isPending || wastesTqrv.isPending) {
        return pendingUseResult;
    }

    const continuingWastes = wastesTqrv.data.filter((maybe): maybe is Waste => {
        if (!maybe) {
            return false
        }
        const rv = !!maybe.destination?.id && (maybe.destination.id !== bundleDestinationId);
        return rv;
    });

    return successUseResult(continuingWastes);
}

export function isBundleMochikomi(bundle: Bundle) {
    return !bundle.job.code;
}

export const deadbeefBundle: BundleStub = {
    id: DEADBEEF,
    displayName: DEADBEEF,
    code: DEADBEEF,
} as const;
